import React from "react";
import { useHistory } from "react-router-dom";
import { useStyles } from "./OrdersHistory.styles";
import { useTranslation } from "react-i18next";
import { OrderHistoryItem } from "../../pages/OrdersHistoryPage/OrderHistoryTypes";
import Currency from "../../shared/components/currency/Currency";
import { getDateInfo } from "../../shared/dates";
import OrderTimeLine from "../common/order-time-line/order-time-line.component";
import CircleLoader from "../common/loader/circle-loader.component";
import Loader from "../common/loader/loader.component";
import { ReactComponent as EmptyIcon } from "../../assets/empty.svg";
import OrdersHistoryFilter, {
  OrdersHistoryFilterValue,
} from "./OrdersHistoryFilter/OrdersHistoryFilter";
import { OrderState } from "../../pages/OrderDetailsPage/OrderDetailsTypes";

interface OrdersHistoryProps {
  items: OrderHistoryItem[];
  totalItems: number;
  canLoadMore: boolean;
  isLoading: boolean;
  isFetchingMore: boolean;
  loadMore: () => void;
  filter: OrdersHistoryFilterValue;
  setFilter: (value: OrdersHistoryFilterValue) => void;
}

const OrdersHistory: React.FC<OrdersHistoryProps> = ({
  items,
  totalItems,
  canLoadMore,
  isLoading,
  isFetchingMore,
  loadMore,
  filter,
  setFilter,
}) => {
  const { t } = useTranslation();
  const dir = t("css.side") as "rtl" | "ltr";
  const classes = useStyles({ dir });
  const history = useHistory();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const formatOrderDate = (dateString: string) => {
    const dateInfo = getDateInfo(dateString);
    const customFormattedDate = dateInfo?.cancelledOn;
    return customFormattedDate;
  };

  const onOrderDetails = (orderId: string) => {
    history.push(`/account/orders/${orderId}`);
  };

  return (
    <div className={classes.ordersHistoryContainer}>
      <span className={`h2 ${classes.ordersHistoryTitle}`}>
        {t("account.Orders")}
      </span>

      <div className="account-order-reg">
        <div className={classes.ordersHistoryContent}>
          <div className={classes.orderHistoryHeader}>
            <span className={classes.ordersHistoryHeaderTitles}>
              {t("account.confirmed")} {isLoading ? "" : totalItems}
            </span>
            <OrdersHistoryFilter value={filter} onChange={setFilter} />
          </div>

          {isLoading ? (
            <div className={classes.tableLoader}>
              <Loader size={64} color={"#B73232"} />
            </div>
          ) : (
            <>
              <table className={classes.ordersHistoryTable}>
                <thead className={classes.ordersHistoryTableHead}>
                  <tr className={classes.ordersHistoryHeaderRow}>
                    <th className={classes.ordersHistoryTableHeaders}>
                      {t("account.Number")}
                    </th>
                    <th className={classes.ordersHistoryTableHeaders}>
                      {t("account.Amount")}
                    </th>
                    <th className={classes.ordersHistoryTableHeaders}>
                      {t("account.deliveryDateAndTime")}
                    </th>
                    <th className={classes.ordersHistoryTableHeaders}>
                      {t("account.Status")}
                    </th>
                    <th className={classes.ordersHistoryTableHeaders}></th>
                  </tr>
                </thead>

                {items?.length ? (
                  <tbody>
                    {items?.map((item) => {
                      const isCancelled =
                        item?.state?.toLocaleLowerCase() ===
                        OrderState.CANCELLED;

                      return (
                        <tr key={item.id} className={classes.ordersHistoryRow}>
                          {console.log({ state: item?.state })}
                          <td className={classes.cellNumber}>{item?.code}</td>

                          <td className={classes.cellAmount}>
                            <div>
                              <Currency />
                              {(item?.totalWithTax / 100).toFixed(2)}
                            </div>
                            {item?.subOrders?.length > 1 && !isCancelled ? (
                              <span className={classes.cellAmountWithSubOrders}>
                                {t("account.includesMultipleDeliveries")}
                              </span>
                            ) : null}
                          </td>

                          <td className={classes.cellDelivery}>
                            <div className={classes.deliveryWrapper}>
                              {!isCancelled ? (
                                <>
                                  {item?.subOrders?.map((subOrder, index) => {
                                    const dateInfo = getDateInfo(
                                      subOrder?.deliveryTime
                                    );
                                    const customFormattedDate =
                                      dateInfo?.customFormattedDate;
                                    const isLast =
                                      index === item?.subOrders?.length - 1;
                                    return (
                                      <div
                                        className={
                                          isLast
                                            ? classes.deliveryDateDiv
                                            : classes.deliveryDateDivWithBorderBottom
                                        }
                                        key={index}
                                      >
                                        <span
                                          className={
                                            classes.deliveryDateFormattedDate
                                          }
                                        >
                                          {customFormattedDate}
                                        </span>
                                        <span
                                          className={
                                            classes.deliveryDateTimewindow
                                          }
                                        >
                                          {t("account.estimating")}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </>
                              ) : (
                                <span className={classes.deliveryCanceledMsg}>
                                  {t("account.orderCanceledOn", {
                                    date: formatOrderDate(item?.updatedAt),
                                  })}
                                </span>
                              )}
                            </div>
                          </td>

                          <td className={classes.cellStatus}>
                            <div className={classes.deliveryWrapper}>
                              {item?.state?.toLowerCase() === "cancelled" ? (
                                <OrderTimeLine state={item?.state} />
                              ) : (
                                item?.subOrders?.map((subOrder, index) => {
                                  const isLast =
                                    index === item.subOrders.length - 1;
                                  return (
                                    <div
                                      className={
                                        isLast
                                          ? classes.statusDiv
                                          : classes.statusDivWithBorderBottom
                                      }
                                      key={index}
                                    >
                                      <OrderTimeLine state={subOrder?.state} />
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </td>

                          <td className={classes.cellDetails}>
                            <span onClick={() => onOrderDetails(item?.code)}>
                              {t("account.details")}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                ) : null}
              </table>
              {!items?.length ? (
                <>
                  <span className={classes.ordersHistoryEmptyList}>
                    {t("account.noOrdersFound")}
                    <EmptyIcon />
                  </span>
                </>
              ) : null}
              {canLoadMore && (
                <div>
                  <button
                    className={classes.loadMoreBtn}
                    onClick={loadMore}
                    disabled={isLoading || isFetchingMore}
                  >
                    {!isFetchingMore ? (
                      t("account.loadMore")
                    ) : (
                      <CircleLoader size={16} color="#80BB34" />
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrdersHistory;
